import React, { useState } from 'react';
import { getRoleName, useAuth } from '../../context/AuthContext';
import { AuthAPI } from '../../services/api';

function PageHeader({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="bg-white border-b border-gray-100 px-6 py-3.5 sticky top-0 z-10">
      <p className="text-[15px] font-black text-gray-900 tracking-tight">{title}</p>
      <p className="text-[11px] text-gray-400 font-medium mt-0.5">{desc}</p>
    </div>
  );
}

function EyeIcon({ visible }: { visible: boolean }) {
  if (visible) {
    // eye-off
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.3 20.3 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a20.32 20.32 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  // eye
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function Field({
  label,
  placeholder,
  value,
  type = "text",
  onChange,
  hint,
  error,
  disabled = false,
}: {
  label: string;
  placeholder?:string;
  value: string;
  type?: string;
  onChange: (v: string) => void;
  hint?: string;
  error?: string;
  disabled?: boolean;
}) {
  const [visible, setVisible] = useState<boolean>(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (visible ? 'text' : 'password') : type;

  return (
    <div>
      <label className="block text-[12px] font-bold text-gray-700 mb-1.5">
        {label}
      </label>

      <div className="relative">
        <input
          type={inputType}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full border ${error ? 'border-red-300' : 'border-gray-200'} rounded-lg px-3 py-2.5 ${isPassword ? 'pr-10' : ''} text-[13px] text-gray-900 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed`}
        />

        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible(v => !v)}
            tabIndex={-1}
            className="absolute right-0 top-0 h-full px-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={visible ? 'Hide password' : 'Show password'}
          >
            <EyeIcon visible={visible} />
          </button>
        )}
      </div>

      {error ? (
        <p className="text-[11px] text-red-600 mt-1 font-semibold">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-gray-400 mt-1 font-medium">{hint}</p>
      ) : null}
    </div>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    organisation: user?.organisation || '',
  });

  const set = (key: string) => (val: string) => setForm(f => ({ ...f, [key]: val }));

  const resetForm = () => {
    setForm({
      full_name: user?.full_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      organisation: user?.organisation || '',
    });
  };

  const handleEditClick = () => {
    resetForm();     
    setError('');
    setSaved(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    resetForm();      
    setError('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await AuthAPI.updateProfile({
        full_name: form.full_name,
        phone_number: form.phone,
      });
      await refreshUser(); // pull the fresh user object into context/localStorage
      setSaved(true);
      setIsEditing(false);   // back to view mode
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Could not save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  const [pwErrors, setPwErrors] = useState<{ current?: string; next?: string; confirm?: string; general?: string }>({});

  const setPw = (key: keyof typeof pwForm) => (val: string) => setPwForm(f => ({ ...f, [key]: val }));

  const handlePasswordChange = async () => {
    setPwSaved(false);

    const errors: typeof pwErrors = {};
    if (!pwForm.current) errors.current = 'Please enter your current password.';
    if (!pwForm.next) {
      errors.next = 'Please enter a new password.';
    } else if (pwForm.next.length < 8) {
      errors.next = 'New password must be at least 8 characters.';
    }
    if (!pwForm.confirm) {
      errors.confirm = 'Please confirm your new password.';
    } else if (pwForm.next && pwForm.confirm !== pwForm.next) {
      errors.confirm = 'Passwords do not match.';
    }

    setPwErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setPwSaving(true);
    try {
      await AuthAPI.changePassword({
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      setPwForm({ current: '', next: '', confirm: '' });
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err: any) {
      setPwErrors({ general: err.message || 'Could not change password. Please check your current password and try again.' });
    } finally {
      setPwSaving(false);
    }
  };

  const initials = user?.full_name
    ? user.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || 'MP';

  return (
    <div className="flex-1 overflow-y-auto">
      <PageHeader title="My profile" desc="Manage your personal information" />

      <div className="p-6 max-w-2xl">

        {/* Avatar */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-5 flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-navy flex items-center justify-center text-[22px] font-black text-white flex-shrink-0">
            {initials}
          </div>
          <div>
            <p className="text-[14px] font-black text-gray-900">{user?.full_name || 'Your name'}</p>
            <p className="text-[12px] text-gray-500 font-medium  mt-0.5"><span className='capitalize'>{getRoleName(user?.role)}</span> · {user?.email}</p>
            <div className="flex items-center gap-1.5 mt-2">
              <div className={`w-2 h-2 rounded-full ${user?.is_verified ? 'bg-green-500' : 'bg-amber-400'}`} />
              <span className="text-[11px] font-medium text-gray-500">
                {user?.is_verified ? 'Email verified' : 'Email not verified'}
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[13px] font-black text-gray-900">Personal information</p>
            {!isEditing && (
              <button
                onClick={handleEditClick}
                className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-bold text-navy border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              >
                <i className="ti ti-pencil text-[13px]" aria-hidden="true" />
                Edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            <Field
              label="Full name"
              placeholder="Enter your full name"
              value={form.full_name}
              onChange={set('full_name')}
              disabled={!isEditing}
            />
            <Field
              label="Email address"
              value={form.email}
              onChange={set('email')}
              type="email"
              disabled
              hint=""
            />
            <Field
              label="Phone number"
              value={form.phone}
              onChange={set('phone')}
              type="tel"
              disabled={!isEditing}
            />
            {getRoleName(user?.role) !== 'researcher' && (
              <Field
                label="Organisation"
                value={form.organisation}
                onChange={set('organisation')}
                disabled={!isEditing}
              />
            )}
          </div>
        </div>

        {/* Account info — read only */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-5">
          <p className="text-[13px] font-black text-gray-900 mb-4">Account details</p>
          <div className="grid grid-cols-2 gap-3">
            {[
           { label: 'Role', value: getRoleName(user?.role) || '' },
              { label: 'Member since', value: user?.date_joined ? new Date(user.date_joined).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '' },
              { label: 'Status', value: user?.is_verified ? 'Verified' : 'Unverified' },
            ].map((f:any) => (
              <div key={f.label} className="bg-gray-50 rounded-lg px-3 py-2.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">{f.label}</p>
                <p className="text-[13px] font-semibold text-gray-800 capitalize">{f.value}</p>
              </div>
            ))}
          </div>
        </div>

       

        {/* Save / Cancel — only visible in edit mode */}
        {isEditing && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 bg-navy text-white text-[13px] font-black rounded-lg hover:bg-navy-dark transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-5 py-2.5 bg-white text-gray-700 text-[13px] font-black rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            {error && (
              <span className="text-[12px] font-semibold text-red-600">{error}</span>
            )}
          </div>
        )}

        {/* Saved confirmation — shown after leaving edit mode */}
        {!isEditing && saved && (
          <div className="flex items-center gap-1.5 text-green-600">
            <i className="ti ti-check text-[14px]" aria-hidden="true" />
            <span className="text-[12px] font-semibold">Saved successfully</span>
          </div>
        )}
       {/* Change password */}
        <div className="bg-white border border-gray-100 mt-4 rounded-xl p-5 mb-5">
          <p className="text-[13px] font-black text-gray-900 mb-4">Change password</p>
          <div className="grid grid-cols-1 gap-4 max-w-sm">
            <Field
              label="Current password"
              placeholder="Enter your current password"
              value={pwForm.current}
              onChange={setPw('current')}
              type="password"
              error={pwErrors.current}
            />
            <Field
              label="New password"
             placeholder="Enter a new password"
              value={pwForm.next}
              onChange={setPw('next')}
              type="password"
              hint="At least 8 characters"
              error={pwErrors.next}
            />
            <Field
              label="Confirm new password"
              placeholder="Re-enter your new password"
              value={pwForm.confirm}
              onChange={setPw('confirm')}
              type="password"
              error={pwErrors.confirm}
            />
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handlePasswordChange}
              disabled={pwSaving}
              className="px-5 py-2.5 bg-navy text-white text-[13px] font-black rounded-lg hover:bg-navy-dark transition-colors disabled:opacity-60"
            >
              {pwSaving ? 'Updating...' : 'Update password'}
            </button>
            {pwSaved && (
              <div className="flex items-center gap-1.5 text-green-600">
                <i className="ti ti-check text-[14px]" aria-hidden="true" />
                <span className="text-[12px] font-semibold">Password updated</span>
              </div>
            )}
            {pwErrors.general && (
              <span className="text-[12px] font-semibold text-red-600">{pwErrors.general}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}